import { useCallback, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store/store';
import { getSchedule } from '../../../store/reducers/ClinicSettingsDetailSlice';
import { DEFAULT_SCHEDULE, weeklyScheduleToLocal } from './schedule';
import type { ScheduleDay } from './schedule';
import type { ScenarioType } from './callSettings';
import type { ScenarioSettingsDomain } from './settingsDomainTypes';
import { getScenarioValues } from './scenarioSettingsRequests';

const SCENARIO_TYPES: readonly ScenarioType[] = [
  'slot',
  'notification',
  'dispensarization',
  'form',
];

export const useScenarioCallSchedule = (
  clinicId: string | undefined,
  domain: ScenarioSettingsDomain,
) => {
  const dispatch = useAppDispatch();
  const { scenario, callSchedule, dirty, callScheduleIdRef, requests } = domain;
  const [callScheduleLoading, setCallScheduleLoading] = useState(true);
  const activeScenarioRef = useRef(scenario.scenarioType);
  activeScenarioRef.current = scenario.scenarioType;

  const displayCallSchedule = (
    targetType: ScenarioType,
    scheduleId: string | null,
    values: { readonly schedule: ScheduleDay[]; readonly holidays: string[] },
  ) => {
    if (activeScenarioRef.current !== targetType) return;
    const { schedule, holidays } = values;
    callScheduleIdRef.current = scheduleId;
    callSchedule.setSchedule(schedule);
    callSchedule.setHolidays(holidays);
    dirty.updateCallScheduleSnapshot(schedule, holidays);
    setCallScheduleLoading(false);
  };

  const applyCallSchedule = (
    targetType: ScenarioType,
    scheduleId: string | null,
    values: { readonly schedule: ScheduleDay[]; readonly holidays: string[] },
  ) => {
    const { schedule, holidays } = values;
    scenario.updateServerScheduleSnapshot(
      targetType,
      scheduleId,
      schedule,
      holidays,
    );
    const cached = scenario.getCached(targetType);
    if (cached) {
      scenario.cacheValues(targetType, {
        ...cached,
        callsScheduleId: scheduleId,
        callSchedule: schedule,
        callHolidays: holidays,
      });
    }
    displayCallSchedule(targetType, scheduleId, values);
  };

  const loadCallSchedule = (
    scheduleId: string | null | undefined,
    targetType: ScenarioType,
  ) => {
    const fallback = {
      schedule: DEFAULT_SCHEDULE.map((day) => ({ ...day })),
      holidays: [],
    };
    if (!scheduleId) {
      applyCallSchedule(targetType, null, fallback);
      return;
    }
    if (activeScenarioRef.current === targetType) {
      callScheduleIdRef.current = scheduleId;
      setCallScheduleLoading(true);
    }

    dispatch(getSchedule(scheduleId))
      .unwrap()
      .then((dao) => {
        applyCallSchedule(targetType, scheduleId, weeklyScheduleToLocal(dao));
      })
      .catch(() => {
        // The thunk exposes the request error; keep the established default schedule.
        displayCallSchedule(targetType, null, fallback);
      });
  };

  const fetchScenarioSettings = (type: ScenarioType) => {
    if (type === 'slot') return;
    requests
      .loadScenarioSettings(type)
      .then((data) => {
        if (!data) return;
        const values = {
          ...getScenarioValues(type, data),
          callsScheduleId: data.callsScheduleId ?? null,
          callSchedule: null,
          callHolidays: null,
        };
        scenario.applyLoadedValues(type, values);
        scenario.setServerSnapshot(type, values);
        loadCallSchedule(data.callsScheduleId, type);
      })
      .catch(() => {
        // The thunk exposes the request error without changing existing scenario values.
        if (activeScenarioRef.current === type) setCallScheduleLoading(false);
      });
  };

  const handleScenarioTypeChange = useCallback(
    (newType: string) => {
      const next = SCENARIO_TYPES.find((type) => type === newType);
      if (!next || next === scenario.scenarioType) return;

      scenario.cacheCurrentValuesWithSchedule(
        callScheduleIdRef.current,
        callSchedule.schedule,
        callSchedule.holidays,
      );
      activeScenarioRef.current = next;
      scenario.setScenarioType(next);
      const cacheHit = scenario.applyFromCache(next);

      if (cacheHit && !cacheHit.settingsLoaded) {
        setCallScheduleLoading(true);
        fetchScenarioSettings(next);
      } else if (cacheHit) {
        callScheduleIdRef.current = cacheHit.callsScheduleId;
        if (cacheHit.callSchedule && cacheHit.callHolidays) {
          callSchedule.setSchedule(cacheHit.callSchedule);
          callSchedule.setHolidays(cacheHit.callHolidays);
          dirty.updateCallScheduleSnapshot(
            cacheHit.callSchedule,
            cacheHit.callHolidays,
          );
          setCallScheduleLoading(false);
        } else {
          loadCallSchedule(cacheHit.callsScheduleId, next);
        }
      } else if (clinicId) {
        fetchScenarioSettings(next);
      }
    },
    [
      clinicId,
      scenario.scenarioType,
      scenario.cacheCurrentValuesWithSchedule,
      scenario.setScenarioType,
      scenario.applyFromCache,
      callSchedule.schedule,
      callSchedule.holidays,
    ],
  );

  return { callScheduleLoading, loadCallSchedule, handleScenarioTypeChange };
};
